import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';

// Fix for default marker icons in Leaflet with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface DestinationResult {
  id: string;
  city: string;
  country: string;
  country_code: string;
  region: string;
  lat: number;
  lon: number;
  tagline: string;
  budget_level?: string;
}

interface DestinationMapProps {
  destinations: DestinationResult[];
  selectedId?: string;
  onSelect: (destination: DestinationResult) => void;
  className?: string;
}

const REGION_COLORS: Record<string, string> = {
  Europe: '#3B82F6',
  Asia: '#EF4444',
  Americas: '#22C55E',
  Africa: '#F97316',
  Oceania: '#A855F7',
  'Middle East': '#F59E0B',
};

const getRegionColor = (region: string): string => {
  return REGION_COLORS[region] || '#6B7280';
};

const CUSTOM_COLOR = '#06B6D4';

const createMarkerIcon = (
  city: string,
  region: string,
  isSelected: boolean,
  isCustom: boolean = false
): L.DivIcon => {
  const color = isCustom ? CUSTOM_COLOR : getRegionColor(region);
  const size = isSelected ? 32 : 24;
  const letter = isCustom ? '+' : city.charAt(0).toUpperCase();
  const borderStyle = isCustom ? 'border: 2px dashed white;' : 'border: 2px solid white;';
  const ringStyle = isSelected
    ? `box-shadow: 0 0 0 3px white, 0 0 0 5px ${color}; transform: scale(1.1);`
    : '';

  return L.divIcon({
    className: 'custom-destination-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: ${isSelected ? 14 : 12}px;
        ${borderStyle}
        ${ringStyle}
        transition: transform 0.2s ease;
      ">${letter}</div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
  });
};

interface FitBoundsProps {
  destinations: DestinationResult[];
}

const FitBounds: React.FC<FitBoundsProps> = ({ destinations }) => {
  const map = useMap();

  useEffect(() => {
    if (destinations.length === 0) return;

    const bounds = L.latLngBounds(
      destinations.map((d) => [d.lat, d.lon] as L.LatLngTuple)
    );

    map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 12,
    });
  }, [destinations, map]);

  return null;
};

export const DestinationMap: React.FC<DestinationMapProps> = ({
  destinations,
  selectedId,
  onSelect,
  className = '',
}) => {
  const { t } = useTranslation('destinations');

  const markers = useMemo(
    () =>
      destinations.map((destination) => ({
        destination,
        icon: createMarkerIcon(
          destination.city,
          destination.region,
          destination.id === selectedId,
          destination.id.startsWith('custom-')
        ),
      })),
    [destinations, selectedId]
  );

  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      style={{ height: '100%', width: '100%' }}
    >
      <MapContainer
        center={[20, 0]}
        zoom={2}
        minZoom={2}
        maxZoom={18}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {destinations.length > 0 && <FitBounds destinations={destinations} />}

        {markers.map(({ destination, icon }) => (
          <Marker
            key={destination.id}
            position={[destination.lat, destination.lon]}
            icon={icon}
          >
            <Popup>
              <div className="min-w-[180px]">
                <p className="font-bold text-gray-900 text-sm">
                  {destination.city}
                </p>
                <p className="text-xs text-gray-600">{destination.country}</p>
                <p className="text-xs text-gray-500 italic mt-1">
                  {destination.tagline}
                </p>
                <button
                  onClick={() => onSelect(destination)}
                  className="mt-2 w-full px-3 py-1.5 bg-cyan-500 text-white text-xs font-medium rounded hover:bg-cyan-600 transition-colors"
                >
                  {t('actions.viewProfile')}
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
